import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, CreditCard, ShieldCheck, Info } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { getClientMe } from '@/services/client';

export default function Cards() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken);

  const { data: me, isLoading } = useQuery({
    queryKey: ['client/me'],
    queryFn: getClientMe,
    enabled: !!token,
  });

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  const cards = me?.user.cards ?? [];

  return (
    <div className="pt-4 pb-8">
      <div className="px-4 mb-4 flex items-center gap-2">
        <Link to="/profile" className="btn-ghost -ml-2 px-2">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-2xl font-semibold">Мои карты</h1>
      </div>

      <div className="px-4 space-y-3">
        {isLoading && <div className="card p-6 text-center text-slate-500">Загрузка…</div>}

        {!isLoading && cards.length === 0 && (
          <div className="card p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2">
              <CreditCard size={24} />
            </div>
            <div className="font-medium mb-1">Сохранённых карт нет</div>
            <div className="text-xs text-slate-500">
              При оплате заказа онлайн через ЮKassa вы сможете сохранить карту, чтобы платить в один клик.
            </div>
          </div>
        )}

        {cards.map((c) => (
          <div key={c.id} className="card p-4 flex items-center gap-3">
            <div className="w-12 h-8 rounded bg-gradient-to-br from-indigo-500 to-brand-600 text-white flex items-center justify-center shrink-0">
              <CreditCard size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">
                {brandLabel(c.brand)} •• {c.last4}
              </div>
              {c.isDefault && (
                <div className="text-[11px] uppercase tracking-wide text-brand-600">
                  По умолчанию
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="card p-4 flex items-start gap-3 bg-slate-50/60">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div className="text-xs text-slate-600">
            Данные карт храним не мы, а ЮKassa. Вы подтверждаете каждый платёж в банковском приложении или 3-D Secure.
          </div>
        </div>

        <div className="card p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Info size={18} />
          </div>
          <div className="text-xs text-slate-600">
            Добавить карту можно на этапе оплаты: выберите «Онлайн (ЮKassa)» при оформлении заказа и отметьте «Сохранить карту».
          </div>
        </div>
      </div>
    </div>
  );
}

function brandLabel(b: string): string {
  const v = b?.toLowerCase() ?? '';
  if (v.includes('mastercard')) return 'MasterCard';
  if (v.includes('visa')) return 'Visa';
  if (v.includes('mir')) return 'МИР';
  return b || 'Карта';
}
